import {
  paginateDtoolsPdfPackage,
  restoreDtoolsPdfPackagePagination,
} from "@/lib/dtoolsPdfPrintPagination";
import { sanitizeProposalPdfDocumentTitle, proposalPdfDownloadFilename } from "@/lib/proposalPdfFilename";
import { prepareProposalPdfForExport, releaseExportCanvas } from "@/lib/proposalPdfExportPrepare";
import {
  detectProposalPdfExportBrowser,
  isSafariBrowser,
  measureProposalPdfPageDimensions,
  planProposalPdfExportScale,
  PROPOSAL_PDF_TOO_LARGE_MESSAGE,
  type ProposalPdfPageDimensions,
} from "@/lib/proposalPdfExportScale";
import {
  logProposalPdfExportDiagnostics,
  PROPOSAL_PDF_JSPDF_RETENTION_NOTE,
} from "@/lib/proposalPdfExportDiagnostics";
import {
  findUnsupportedProposalPdfStyles,
  normaliseProposalPdfExportStyles,
  prepareProposalPdfExportClone,
} from "@/lib/proposalPdfExportNormalize";
import {
  assessProposalPdfQrReadiness,
  prepareProposalPdfQrForClone,
} from "@/lib/proposalPdfExportCanvas";
import { PDF_A4_WIDTH_PX, PDF_QUOTATION_PAGE_BG } from "@/lib/proposalPdfQuotationLayout";
import {
  customerMessageForProposalPdfExportError,
  logProposalPdfExportFailure,
  classifyProposalPdfExportError,
  PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES,
} from "@/lib/proposalPdfExportErrors";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

export type ProposalPdfExportStatus = "generating" | "optimizing";

/** How to deliver the generated PDF. Default `download` preserves customer/staff download buttons. */
export type ProposalPdfDelivery = "download" | "blob";

export type DownloadProposalPdfInput = {
  proposalRef?: string | null;
  onProgress?: (current: number, total: number) => void;
  onStatus?: (status: ProposalPdfExportStatus) => void;
  /**
   * `download` — trigger file download (default).
   * `blob` — return a Blob for in-browser preview (no automatic download).
   */
  delivery?: ProposalPdfDelivery;
};

let exportInFlight: Promise<Blob | void> | null = null;

function collectProposalPdfPageElements(root: HTMLElement): HTMLElement[] {
  const pages: HTMLElement[] = [];

  const cover = root.querySelector<HTMLElement>('[data-testid="dtools-pdf-cover-page"]');
  if (cover) pages.push(cover);

  pages.push(...root.querySelectorAll<HTMLElement>(".pdf-print-page"));

  const closing = root.querySelector<HTMLElement>('[data-testid="dtools-pdf-closing-page"]');
  if (closing) pages.push(closing);

  return pages;
}

function pageIdentifier(page: HTMLElement, index: number): string {
  return (
    page.getAttribute("data-testid") ||
    page.getAttribute("data-pdf-pagination-title") ||
    page.className?.toString?.()?.slice?.(0, 60) ||
    `page-${index}`
  );
}

function showPackageForExport(pkg: HTMLElement): void {
  pkg.classList.remove("dtools-proposal-pdf-package-screen-hidden", "hidden");
  pkg.classList.add("dtools-proposal-pdf-package-print-ready");
  pkg.style.position = "fixed";
  pkg.style.left = "-10000px";
  pkg.style.top = "0";
  pkg.style.width = `${PDF_A4_WIDTH_PX}px`;
  pkg.style.visibility = "visible";
  pkg.style.pointerEvents = "none";
  pkg.style.overflow = "visible";
  pkg.style.background = PDF_QUOTATION_PAGE_BG;
}

function hidePackageAfterExport(pkg: HTMLElement): void {
  restoreDtoolsPdfPackagePagination(pkg);
  pkg.classList.remove("dtools-proposal-pdf-package-print-ready");
  pkg.classList.add("dtools-proposal-pdf-package-screen-hidden");
  pkg.removeAttribute("style");
}

function countFailedPdfImages(root: HTMLElement): number {
  return root.querySelectorAll("[data-pdf-image-failed]").length;
}

async function capturePageToPdf(
  page: HTMLElement,
  pdf: import("jspdf").jsPDF,
  pageIndex: number,
  html2canvas: typeof import("html2canvas").default,
  scale: number,
  proposalRef: string | null,
  pageCount: number,
): Promise<void> {
  if (pageIndex > 0) pdf.addPage();

  let canvas: HTMLCanvasElement;
  try {
    canvas = await html2canvas(page, {
      scale,
      useCORS: true,
      allowTaint: false,
      backgroundColor: PDF_QUOTATION_PAGE_BG,
      logging: false,
      imageTimeout: 15000,
      onclone: (clonedDoc, clonedElement) => {
        prepareProposalPdfExportClone(clonedDoc, clonedElement);
        const qrTransfer = prepareProposalPdfQrForClone(page, clonedElement);
        if (import.meta.env.DEV && qrTransfer.attempted > 0) {
          console.info("[proposal-pdf-export] QR canvas transfer", {
            proposalRef,
            pageIndex,
            ...qrTransfer,
          });
        }
        if (qrTransfer.attempted > 0 && qrTransfer.transferred === 0) {
          console.warn("[proposal-pdf-export] QR canvas transfer failed — PDF QR may be blank", {
            proposalRef,
            pageIndex,
            ...qrTransfer,
          });
        }
        for (const img of clonedElement.querySelectorAll<HTMLImageElement>("img[data-pdf-image-failed]")) {
          img.style.display = "none";
        }
      },
    });
  } catch (error) {
    const unsupportedHits = import.meta.env.DEV ? findUnsupportedProposalPdfStyles(page) : [];
    logProposalPdfExportFailure(error, {
      pageIndex,
      pageCount,
      pageTestId: pageIdentifier(page, pageIndex),
      proposalRef,
      kind: classifyProposalPdfExportError(error),
      unsupportedHits,
    });
    throw new Error(customerMessageForProposalPdfExportError(error, { pageIndex, pageCount, proposalRef }));
  }

  try {
    const imgData = canvas.toDataURL("image/png");
    let renderWidth = A4_WIDTH_MM;
    let renderHeight = (canvas.height * renderWidth) / canvas.width;
    if (renderHeight > A4_HEIGHT_MM) {
      renderHeight = A4_HEIGHT_MM;
      renderWidth = (canvas.width * renderHeight) / canvas.height;
    }
    pdf.addImage(imgData, "PNG", 0, 0, renderWidth, renderHeight, undefined, "NONE");

    // Raster capture loses <a href>; add a real PDF link annotation for Link B on the closing page.
    const closingLink = page.querySelector<HTMLAnchorElement>(
      '[data-testid="dtools-pdf-closing-url"], a[data-pdf-external-link="true"]',
    );
    const href = (closingLink?.getAttribute("href") ?? "").trim();
    if (href && closingLink) {
      const pageRect = page.getBoundingClientRect();
      const linkRect = closingLink.getBoundingClientRect();
      if (pageRect.width > 0 && pageRect.height > 0 && linkRect.width > 0 && linkRect.height > 0) {
        const scaleX = renderWidth / pageRect.width;
        const scaleY = renderHeight / pageRect.height;
        const x = (linkRect.left - pageRect.left) * scaleX;
        const y = (linkRect.top - pageRect.top) * scaleY;
        const w = Math.max(linkRect.width * scaleX, 8);
        const h = Math.max(linkRect.height * scaleY, 4);
        pdf.link(x, y, w, h, { url: href });
      }
    }
  } catch (error) {
    logProposalPdfExportFailure(error, {
      pageIndex,
      pageCount,
      pageTestId: pageIdentifier(page, pageIndex),
      proposalRef,
      kind: classifyProposalPdfExportError(error),
    });
    throw new Error(customerMessageForProposalPdfExportError(error, { pageIndex, pageCount, proposalRef }));
  } finally {
    releaseExportCanvas(canvas);
  }
}

function measurePages(pages: HTMLElement[]): ProposalPdfPageDimensions[] {
  return pages.map(measureProposalPdfPageDimensions);
}

async function runProposalPdfExport(input: DownloadProposalPdfInput): Promise<Blob | void> {
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const pkg = document.querySelector(".dtools-proposal-pdf-package");
  if (!(pkg instanceof HTMLElement)) {
    throw new Error("Proposal PDF is not ready yet. Please try again in a moment.");
  }

  const previousTitle = document.title;
  const proposalRef = input.proposalRef ?? pkg.dataset.proposalRef ?? null;
  const printTitle = sanitizeProposalPdfDocumentTitle(proposalRef ?? "proposal");
  document.title = printTitle;
  const delivery: ProposalPdfDelivery = input.delivery ?? "download";

  showPackageForExport(pkg);
  try {
    if (pkg.dataset.pdfPaginationActive === "true") {
      restoreDtoolsPdfPackagePagination(pkg);
    }
    paginateDtoolsPdfPackage(pkg);
    await prepareProposalPdfForExport(pkg);
    // Pre-capture normalisation on the off-screen package (clone pass still runs in onclone).
    normaliseProposalPdfExportStyles(pkg);

    const qrReadiness = assessProposalPdfQrReadiness(pkg);
    if (qrReadiness.required && !qrReadiness.ready) {
      // Final ensure pass — generate PNG data-URL QR if React effect has not finished.
      const { ensureProposalPdfQrImages } = await import("@/lib/proposalPdfExportCanvas");
      const ensured = await ensureProposalPdfQrImages(pkg);
      if (ensured.required && !ensured.ready) {
        throw new Error(
          ensured.reason
            ? `Proposal PDF QR code could not be generated (${ensured.reason}).`
            : "Proposal PDF QR code could not be generated.",
        );
      }
    }

    const pages = collectProposalPdfPageElements(pkg);
    if (pages.length === 0) {
      throw new Error("No printable proposal pages were found.");
    }

    if (import.meta.env.DEV) {
      const preHits = findUnsupportedProposalPdfStyles(pkg);
      if (preHits.length > 0) {
        console.warn("[proposal-pdf-export] unsupported styles before capture", {
          proposalRef,
          hitCount: preHits.length,
          sample: preHits.slice(0, 8),
        });
      }
    }

    const pageDimensions = measurePages(pages);
    const scalePlan = planProposalPdfExportScale({
      pages: pageDimensions,
      devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
      isSafari: isSafariBrowser(),
    });

    if (scalePlan.hardLimitExceeded) {
      throw new Error(PROPOSAL_PDF_TOO_LARGE_MESSAGE);
    }

    if (scalePlan.optimizing) {
      input.onStatus?.("optimizing");
    } else {
      input.onStatus?.("generating");
    }

    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    pdf.setProperties({
      title: printTitle,
      subject: "Clarity proposal quotation",
      creator: "Clarity Portal",
      author: "Clarity",
    });

    for (let index = 0; index < pages.length; index++) {
      input.onProgress?.(index + 1, pages.length);
      await capturePageToPdf(
        pages[index]!,
        pdf,
        index,
        html2canvas,
        scalePlan.scale,
        proposalRef,
        pages.length,
      );
    }

    let result: Blob | void;
    try {
      if (delivery === "blob") {
        result = pdf.output("blob");
      } else {
        pdf.save(proposalPdfDownloadFilename(proposalRef));
        result = undefined;
      }
    } catch (error) {
      logProposalPdfExportFailure(error, {
        proposalRef,
        pageCount: pages.length,
        kind: "assembly",
      });
      throw new Error(customerMessageForProposalPdfExportError(error, { proposalRef, pageCount: pages.length }));
    }

    const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    logProposalPdfExportDiagnostics({
      browser: detectProposalPdfExportBrowser(),
      pageCount: pages.length,
      selectedScale: scalePlan.scale,
      baseScale: scalePlan.baseScale,
      optimizing: scalePlan.optimizing,
      estimatedPeakCanvasBytes: scalePlan.memory.peakCanvasBytes,
      estimatedTotalImageBytes: scalePlan.memory.totalImageBytes,
      failedImageCount: countFailedPdfImages(pkg),
      durationMs: Math.round(endedAt - startedAt),
    });

    if (import.meta.env.DEV) {
      console.info("[proposal-pdf-export]", PROPOSAL_PDF_JSPDF_RETENTION_NOTE);
    }

    return result;
  } catch (error) {
    if (error instanceof Error) {
      const known =
        error.message === PROPOSAL_PDF_TOO_LARGE_MESSAGE ||
        error.message === PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES.unsupported_styling ||
        error.message === PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES.image_cors ||
        error.message === PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES.memory ||
        error.message === PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES.assembly ||
        error.message === PROPOSAL_PDF_EXPORT_CUSTOMER_MESSAGES.unknown ||
        error.message.startsWith("Proposal PDF is not ready") ||
        error.message.startsWith("No printable proposal") ||
        error.message.startsWith("A proposal PDF is already");
      if (known) throw error;
    }
    logProposalPdfExportFailure(error, { proposalRef });
    throw new Error(customerMessageForProposalPdfExportError(error, { proposalRef }));
  } finally {
    document.title = previousTitle;
    hidePackageAfterExport(pkg);
  }
}

/**
 * Generate and download a proposal PDF directly (html2canvas + jsPDF).
 * Avoids browser print headers/footers that CSS cannot suppress in Safari.
 */
export async function downloadProposalPdf(input: DownloadProposalPdfInput = {}): Promise<void> {
  if (exportInFlight) {
    throw new Error("A proposal PDF is already being generated. Please wait for it to finish.");
  }

  exportInFlight = runProposalPdfExport({ ...input, delivery: "download" }).finally(() => {
    exportInFlight = null;
  });

  await exportInFlight;
}

/** Generate the same quotation PDF as a Blob (no automatic download). */
export async function generateProposalPdfBlob(
  input: Omit<DownloadProposalPdfInput, "delivery"> = {},
): Promise<Blob> {
  if (exportInFlight) {
    throw new Error("A proposal PDF is already being generated. Please wait for it to finish.");
  }

  exportInFlight = runProposalPdfExport({ ...input, delivery: "blob" }).finally(() => {
    exportInFlight = null;
  });

  const blob = await exportInFlight;
  if (!(blob instanceof Blob)) {
    throw new Error("Could not generate proposal PDF.");
  }
  return blob;
}

export type OpenProposalPdfInViewerResult = {
  opened: boolean;
  objectUrl: string;
};

/**
 * Open a PDF Blob in the browser's native viewer (new tab by default).
 * Uses a named File so Safari/Chrome propose `CLARITY - <ref>.pdf` where supported.
 */
export function openProposalPdfBlobInViewer(
  blob: Blob,
  options: { target?: "_blank" | "_self"; revokeAfterMs?: number; filename?: string } = {},
): OpenProposalPdfInViewerResult {
  const filename = options.filename?.trim() || proposalPdfDownloadFilename("proposal");
  const named =
    typeof File !== "undefined"
      ? new File([blob], filename.endsWith(".pdf") ? filename : `${filename}.pdf`, {
          type: "application/pdf",
        })
      : blob;
  const objectUrl = URL.createObjectURL(named);
  const target = options.target ?? "_blank";
  const revokeAfterMs = options.revokeAfterMs ?? 60_000;

  if (target === "_self") {
    window.location.assign(objectUrl);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), revokeAfterMs);
    return { opened: true, objectUrl };
  }

  const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!win) {
    return { opened: false, objectUrl };
  }

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), revokeAfterMs);
  return { opened: true, objectUrl };
}

/** @internal Test helper — whether an export is currently running. */
export function isProposalPdfExportInFlight(): boolean {
  return exportInFlight != null;
}

/** @internal Test helper — clear export mutex between tests. */
export function resetProposalPdfExportForTests(): void {
  exportInFlight = null;
}
